import type { AxiosError } from "axios"
import type { Ref } from "vue"
import { defineStore } from "pinia"
import { ref } from "vue"
import { axios } from "@/axios"

export const useHealthStore = defineStore("health", () => {
  const isBackendReachable: Ref<boolean> = ref(true)
  const isCheckingHealth: Ref<boolean> = ref(false)

  const sleep = (ms: number) => new Promise(res => setTimeout(res, ms))

  async function checkHealth() {
    if (isCheckingHealth.value) {
      return
    }

    const retries = 1
    isCheckingHealth.value = true

    try {
      for (let attempt = 0; attempt <= retries; attempt++) {
        try {
          const res = await axios.get("/api/system/health")
          isBackendReachable.value = true
          return { reachable: true, status: res.status }
        }
        catch (err) {
          const e = err as AxiosError

          // Server was reached
          if (e.response) {
            isBackendReachable.value = true
            return { reachable: true, status: e.response.status }
          }

          // No response (timeout, DNS, CORS, offline)
          if (attempt < retries) {
            const backoff = 500 * (2 ** attempt) + Math.random() * 100 // jitter
            await sleep(backoff)
            continue
          }

          // Exhausted retries
          isBackendReachable.value = false
          return { reachable: false }
        }
      }
    }
    finally {
      isCheckingHealth.value = false
    }
  }

  function setIsBackendReachable(value: boolean) {
    isBackendReachable.value = value
  }

  return {
    isBackendReachable,
    isCheckingHealth,
    checkHealth,
    setIsBackendReachable,
  }
})
